import { useMemo } from 'react';
import { MessageSquare, Mail, Share2, Facebook, Twitter } from 'lucide-react';

// WhatsApp brand icon (lucide doesn't ship one) — inline SVG keeps deps light.
const WhatsAppIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
    </svg>
);

/**
 * Channel-aware quick share buttons. Each opens the right deep-link with a
 * pre-filled message that includes the player's referral URL and the *current*
 * (possibly boosted) bonus amounts so they don't have to type anything.
 */
export const QuickShareBar = ({ referralLink, referrerBonus, refereeBonus, loadThreshold = 20, boostActive }) => {
    const messages = useMemo(() => {
        const load = Number(loadThreshold) || 20;
        const youGet = referrerBonus ? `I'll get $${referrerBonus} Free Play` : "I'll get a bonus";
        const friendPart = refereeBonus ? ` and you'll get $${refereeBonus} in Free Play` : '';
        const boostBlurb = boostActive ? ' 🔥 Limited-time bonus boost is live!' : '';

        const long = `Join me on BETohalics — sign up with my link and load $${load}. ${youGet} for inviting you${friendPart}.${boostBlurb} ${referralLink}`;
        const short = `Join BETohalics with my link & load $${load} to get started:${boostBlurb} ${referralLink}`;
        const tweet = `Join BETohalics with my link — load $${load} and let's play${boostBlurb}`;
        return { long, short, tweet };
    }, [referralLink, referrerBonus, refereeBonus, loadThreshold, boostActive]);

    if (!referralLink) return null;

    const channels = [
        {
            key: 'sms',
            label: 'Text',
            icon: MessageSquare,
            color: '#39FF14',
            href: `sms:?&body=${encodeURIComponent(messages.short)}`,
        },
        {
            key: 'whatsapp',
            label: 'WhatsApp',
            icon: WhatsAppIcon,
            color: '#25D366',
            href: `https://wa.me/?text=${encodeURIComponent(messages.short)}`,
            target: '_blank',
        },
        {
            key: 'email',
            label: 'Email',
            icon: Mail,
            color: '#FFD700',
            href: `mailto:?subject=${encodeURIComponent('Join me on BETohalics')}&body=${encodeURIComponent(messages.long)}`,
        },
        {
            key: 'facebook',
            label: 'Facebook',
            icon: Facebook,
            color: '#1877F2',
            href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}&quote=${encodeURIComponent(messages.short)}`,
            target: '_blank',
        },
        {
            key: 'twitter',
            label: 'X / Twitter',
            icon: Twitter,
            color: '#FFFFFF',
            href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(messages.tweet)}&url=${encodeURIComponent(referralLink)}`,
            target: '_blank',
        },
    ];

    const nativeShare = async (e) => {
        e.preventDefault();
        if (!navigator.share) return;
        try {
            await navigator.share({
                title: 'BETohalics',
                text: messages.short.replace(referralLink, '').trim(),
                url: referralLink,
            });
        } catch { /* user cancelled */ }
    };
    const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

    return (
        <div className="mt-5 pt-5 border-t border-white/10" data-testid="quick-share-bar">
            <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-white">Quick Share</p>
                <p className="text-xs text-[#A0AAB2]">Pre-filled with your link & bonuses</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                {channels.map((c) => {
                    const Icon = c.icon;
                    return (
                        <a
                            key={c.key}
                            href={c.href}
                            target={c.target}
                            rel={c.target === '_blank' ? 'noopener noreferrer' : undefined}
                            className="group flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg bg-[#0a0a0a] border border-white/10 hover:border-white/30 hover:bg-white/5 transition-colors"
                            data-testid={`quick-share-${c.key}`}
                        >
                            <Icon className="w-5 h-5" style={{ color: c.color }} />
                            <span className="text-xs text-[#A0AAB2] group-hover:text-white transition-colors">{c.label}</span>
                        </a>
                    );
                })}
                {hasNativeShare && (
                    <button
                        type="button"
                        onClick={nativeShare}
                        className="group flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg bg-[#0a0a0a] border border-white/10 hover:border-white/30 hover:bg-white/5 transition-colors"
                        data-testid="quick-share-native"
                    >
                        <Share2 className="w-5 h-5 text-[#00FFFF]" />
                        <span className="text-xs text-[#A0AAB2] group-hover:text-white transition-colors">More…</span>
                    </button>
                )}
            </div>
        </div>
    );
};
